/**
 * The purpose of this file is to gather all the get/put/post/delete calls
 * that are needed in the web client code in a way that hides the details
 * such as client configuration and path templates, so that the client
 * code can use simple calls like getMdfs().
 */
import { globalBackendClient as backendClient } from '@mimir/client-common/globals/backendClient';
import * as api from '@mimir/client-common/util/api';
import type {
  AnalyticsResult,
  AnalyticsSearchQuery,
  AnalyticsSearchResults,
} from '@mimir/common/dto/analyticsModels';
import type { ArchiveOperationRequestDto, ArchiveOperationResponseDto } from '@mimir/common/dto/archiveOperationDto';
import { OperationType } from '@mimir/common/dto/archiveOperationDto';
import type { ReprocessActionType } from '@mimir/common/dto/asyncOperationPrimitives';
import type { CreateTranscriptOptionDto, TenantOpsSummaryReport } from '@mimir/common/dto/asyncOperationModels';
import type { FolderMetadataDto, FolderMetadataUpdatePayload } from '@mimir/common/dto/folderModels';
import type GroupModel from '@mimir/common/dto/groupModel';
import type { CreateGroupDto, UpdateGroupDto } from '@mimir/common/dto/groupModel';
import type { IngestStorageConfigClientDto, UpdateUploadLocationDto } from '@mimir/common/dto/ingestStorageConfigDto';
import type { SearchScrollResponse, LoadedItem, ItemUpdateRequestDto } from '@mimir/common/dto/searchResultHit';
import type { ItemCreationRequestDto } from '@mimir/common/dto/itemCreationRequestDto';
import type { ScheduleHighresDeletionRequestDto } from '@mimir/common/dto/itemModelPrimitives';
import type { StartAnalyticsJobRequestDto } from '@mimir/common/dto/itemModels';
import type { DynamoItemType } from '@mimir/common/dto/itemTypes';
import type { ItemVersionDto as ItemVersion, ItemVersionsHistoryDtoExt, ItemVersionsHistoryDto, LatestItemVersionDto, ItemVersionUpdateDto } from '@mimir/common/dto/itemVersionDto';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { MdfCreationRequest } from '@mimir/common/dto/mdfModels';
import type { PersonImageDto, SimilarPersonsDto } from '@mimir/common/dto/personModel';
import type Relations from '@mimir/common/dto/relationModels';
import type { RelationType } from '@mimir/common/dto/relationsEntity';
import type { RestoreMediaModel } from '@mimir/common/dto/restoreMediaDto';
import type { FeatureFlagDto, UpdateFeatureFlagDto } from '@mimir/common/dto/featureFlagDto';
import type { BookmarkDto, BookmarkFolderWithEtag, BookmarkWriteDto, CreateBookmarkDto, DeleteBookmarkFolderDto, UpdateBookmarkDto } from '@mimir/common/dto/bookmarkDto';
import type { StorageType } from '@mimir/common/dto/storageModel';
import type TenantModel from '@mimir/common/dto/tenantModel';
import type {
  ItemActivityQueryParams,
  TenantSystemActivityQueryParams,
  SystemActivityResponseDto,
  ItemActivityResponseDto,
} from '@mimir/common/dto/systemActivityDtos';
import type {
  Availability,
  AnalyticConfig,
  ArchiveConfig,
  CreateTenantRequestDto,
  TenantConfigDto,
  UpdateTenantConfigDto,
  TenantIdsDto,
  TenantSlimDto
} from '@mimir/common/dto/tenantModel';
import type { TimedMetadataCollection, UpdateTimedMetadataRequestDto } from '@mimir/common/dto/timedMetadataModels';
import type { TransferRequestDto, TransferRequestPostRequestDto } from '@mimir/common/dto/transferRequestDtos';
import type {
  CreateItemUploadLockResponseDto,
  GetItemUploadLockResponseDto,
  UpdateDefaultUploadLocationDto,
  UploadPartDto,
  CreateItemUploadPartsSignedUrlsRequestDto,
  CreateItemUploadPartsSignedUrlsResponseDto,
  CreateItemUploadLockRequestDto,
  UploadLocationId,
  CreateUploadLocationDto
} from '@mimir/common/dto/uploadLocationDto';
import type { UserDto, UpdateUserDto, TenantAdminPostRequestDto, UserModelView, CreateUserDto, UserInfoDto, UserStatusDto } from '@mimir/common/dto/userDto';
import type { UserAvatarUploadDto, UserAvatarUploadUrlRequest, UserSettings } from '@mimir/common/dto/userSettingsModels';
import type { CreateVirtualClipPayload } from '@mimir/common/dto/virtualClipUtil';
import type { VirtualClipModel } from '@mimir/common/dto/virtualClipModel';
import { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import type { HttpQueryParameters, AdditionalParams, ApiCallResult } from '@mimir/common/util/apiClient';
import type { AttachmentType } from '@mimir/common/dto/attachmentPrimitives';
import type { CreateItemAttachmentRequestDto, UpdateItemAttachmentRequestDto, ItemAttachmentWithDownloadUrlDto, AttachmentUploadPreCheckDtO, ItemAttachmentDtoWithUserId } from '@mimir/common/dto/itemAttachmentDto';
import type { UpdateExternalLocationDto, ColdStorageApiCommonDto, ColdStorageEligibleConfigTypes } from '@mimir/common/dto/bucketConfigDto';
import type { MinioConfigDto, UpdateMinioConfigDto, CreateMinioConfigDto } from '@mimir/common/dto/keldaConfigPrimitives';
import type { CreateChangelogDto, ChangelogDto, UpdateChangelogDto } from '@mimir/common/dto/changelogDto';
import type { ExternalStorageVerificationRequest, ExternalStorageVerificationResponse } from '@mimir/common/dto/externalStorageVerificationDto';
import type {
  CountStatisticsResponseDto,
  GetCountStatisticsQueryParams,
  LogStaticRequestDto
} from '@mimir/common/dto/statsDtos';
import type { CatchupIngestStorageRequest } from '@mimir/common/dto/catchUpIngestStorageDto';
import type { AafDto, AafGenerationRequest } from '@mimir/common/dto/aafDto';
import type { NonScheduledLiveItemCreationRequestDto, ScheduledRecordingDto, GetScheduledRecordingsDto, ScheduledRecordingPutRequestDto, ScheduledRecordingCreationRequestDto, RecordingSeriesFullDto, RecordingSeriesCreationDto, RecordingSeriesUpdateDto, RecordingSeriesItemCreationDto } from '@mimir/common/dto/liveRecordingDtos';
import type { NonScheduledLiveItemCreationResponseDto } from '@mimir/common/dto/liveMetadataDtos';
import type {
  LiveItemStateRequestDto,
  LiveRecordingItemStateDto
} from '@mimir/common/dto/liveDtos';
import type { MosAdminGetMessagesType, MosAdminOperationRequestDto, MosMessageQueue } from '@mimir/common/dto/mosDtos';
import type { LiveSourceDtoV2, SourceListKey, UpdateLiveSourceDtoV2, UpdateSelectedDynamicSourceDto } from '@mimir/common/dto/liveSourceDtos';
import type LiveLocationDto from '@mimir/common/dto/LiveLocationDto';
import type { CreateOrUpdateLiveLocationDto } from '@mimir/common/dto/LiveLocationDto';
import type { CreateGeneratedMediaLocationDto, UpdateGeneratedMediaLocationDto } from '@mimir/common/dto/generatedMediaLocationDtos';
import type { RouterDto, UpdateRouterDto } from '@mimir/common/dto/routerControllerDtos';
import type { OptionListDto, OptionListIdsDto, UpdateOptionListDto } from '@mimir/common/dto/optionListDto';
import type { DeduplicationRequestDto, DeduplicationResultDto } from '@mimir/common/dto/deduplicationDtos';
import type { ItemEditStatusCreateDto, ItemEditStatusDeleteDto } from '@mimir/common/dto/itemEditStatusDto';
import type { KeldaPlayoutCommandDto, PlayoutDeviceDto, PlayoutDeviceStatusGetDto, ResetPlayoutDto } from '@mimir/common/dto/playoutDtos';
import type { ExternalShareGetDto, ShareCreationRequestDto, ShareUpdateRequestDto } from '@mimir/common/dto/shareDtos';
import type { ProgressInfo } from '@mimir/common/dto/progress';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { MediaSegmentsResponse } from '@mimir/common/dto/mediaDtos';
import type { NdiSourceDto } from '@mimir/common/dto/ndiDtos';
import type { IngestFileDto } from '@mimir/common/dto/ingestFileDtos';
import type { MeteringResponseDto, MeteringResultSingleTenant } from '@mimir/common/dto/meteringDto';
import type { SearchScrollParamsType } from '@mimir/client-common/util/search';
import type { CancelToken } from 'axios';
import type { ItemMediaUsagesRequestDto } from '@mimir/common/dto/itemMediaUsageDto';
import type { ClipListDto, ClipListUpdateDto } from '@mimir/common/dto/clipListDto';
import type { RenderSequenceDto, RenderSequencePostResponseDto } from '@mimir/common/dto/renderSequence';
import type { CreateSecureApiKeyResponseDto, SecureApiKeyDto, SecureApiKeyModifyRequestDto } from '@mimir/common/dto/secureApiKeyDto';
import type { IPAddressDto } from '@mimir/common/dto/ipAddressDto';
import type { DisplayAspectRatio } from '@mimir/common/types/displayAspectRatio';
import type { EditCommentDto, GetCommentDto, GetCommentsResponseDto, PostCommentRequestDto, ReactionsUpdateDto, UpdateCommentStatusDto } from '@mimir/common/dto/commentDtos';
import type { GetUserTaskResponseDto, GetUserTasksQueryParams, GetUserTasksResponseDto } from '@mimir/common/dto/userTasksDtos';
import type { CreateCustomActionDto, CustomActionDto, UpdateCustomActionDto } from '@mimir/common/dto/customActionDtos';
import { api_completeMultipartUpload, api_createItemUploadLock, api_createItemUploadPartsSignedUrls, api_deleteItemUploadLock, api_getItemUploadLock } from '@mimir/common/api/uploadApi';
import type { TaskInfo } from '@mimir/common/dto/taskInfo';
import type { CropImageRequest } from '@mimir/common/dto/cropImageDtos';
import { api_postCropImage } from '@mimir/client-common/api/cropImageApi';
import type { WebhookDto, CreateWebhookDto, UpdateWebhookDto } from '@mimir/common/dto/webhookDtos';
import type { BatchCreateItemsRequestDto } from '@mimir/common/dto/batchCreateItems';
import type { BatchUpdateItemsRequestDto } from '@mimir/common/dto/batchUpdateItems';
import type { BatchDeleteItemsRequestDto } from '@mimir/common/dto/batchDeleteItems';
import type { CustomIconDto, CustomIconUploadDto } from '@mimir/common/dto/customIconDtos';
import type { CreateCustomIconDto } from '@mimir/common/dto/customIconDtos';
import type { GetKeldaMediaPipelineStatusDto } from '@mimir/common/dto/keldaMediaPipelineStatusDto';
import type { GetItemRelationsParams } from '@mimir/client-common/util/api';
import type { TimelineDto } from '@mimir/common/dto/cutter/timelineDto';
import type { SequenceDetails } from '@mimir/primitive/premiere/sequenceDetails';
import type { CreatePluginDto, PluginDto, UpdatePluginDto } from '@mimir/common/dto/pluginDto';
import type { ItemMetadataUpdateRequestDto } from '@mimir/common/dto/itemMetadataUpdate';

/*****************************************************************************
 * The functions declared here are meant to be used from the UI code only.   *
 * They are NOT SAFE to use from the integration tests and Storage Manager   *
 * as that would introduce a dependency from non UI code to UI code.         *
 *                                                                           *
 * If you need to call a function from the integration tests or              *
 * Storage Manager, you should use the functions in                          *
 * clients/mimir-common/src/util/api.ts instead.                             *
 *****************************************************************************/


/************************ items ************************/

// Type safe variant of getting only some fields of an item
export const getItemFields = async <Key extends keyof LoadedItem>(itemId: string, fields: Key[]) =>
  api.api_getItemFields(backendClient, itemId, fields);

// TODO: Define a correct GetItemResponseDto.
// LoadedItem has client-only fields like 'loading' on it, as well
// as other fields not returned by getItem like timedTranscript.
export const getItem = async (itemId: string, queryParams: HttpQueryParameters = {}): Promise<LoadedItem> => {
  const item = await api.api_getItem(backendClient, itemId, { queryParams });
  item.loaded = true;
  return item;
};

export const getItemComments = async (itemId: string): Promise<GetCommentsResponseDto> => {
  return await api.api_getComments(backendClient, itemId);
};

export const getItemComment = async (itemId: string, commentId: string): Promise<GetCommentDto> => {
  return await api.api_getComment(backendClient, itemId, commentId);
};

export const postItemComment = async (itemId: string, comment: PostCommentRequestDto): Promise<GetCommentDto> => {
  return await api.api_postComment(backendClient, itemId, comment);
};

export const updateItemCommentStatus = async (itemId: string, commentId: string, update: UpdateCommentStatusDto): Promise<void> => {
  await api.api_updateCommentStatus(backendClient, itemId, commentId, update);
};

export const updateItemCommentText = async (itemId: string, commentId: string, update: EditCommentDto): Promise<void> => {
  await api.api_updateCommentText(backendClient, itemId, commentId, update);
};

export const updateItemCommentReaction = async (itemId: string, commentId: string, update: ReactionsUpdateDto): Promise<void> => {
  await api.api_updateCommentReaction(backendClient, itemId, commentId, update);
};

export const deleteItemComment = async (itemId: string, commentId: string): Promise<void> => {
  return await api.api_deleteComment(backendClient, itemId, commentId);
};

export const getClipList = async (itemId: string): Promise<ClipListDto> =>
  api.api_getClipList(backendClient, itemId);

export const undeleteItem = (itemId: string): Promise<any> =>
  api.api_undeleteItem(backendClient, itemId);

export const createPlaceholder = async (item: Partial<ItemCreationRequestDto>) => {
  return api.api_createPlaceholder(backendClient, item);
};

export const createItem = async (
  title: string,
  filename: string,
  fileType: string,
  analyticConfig: AnalyticConfig,
  folderParents: string[] | null,
  parentItemIds: string[] | undefined,
  sequenceDetails: SequenceDetails | undefined,
  metadata: ItemMetaData | undefined,
  isHidden: boolean | undefined,
  isP2Part: boolean | undefined,
  itemType?: DynamoItemType,
  progressInfo?: ProgressInfo,
  options?: AdditionalParams,
  relatedItemIds?: string[]
) =>

  api.api_createItem(backendClient,
    title,
    filename,
    fileType,
    analyticConfig,
    folderParents,
    parentItemIds,
    sequenceDetails,
    metadata,
    isHidden,
    isP2Part,
    itemType,
    progressInfo,
    options,
    relatedItemIds);

export async function batchCreateItems(
  payload: BatchCreateItemsRequestDto,
  options?: AdditionalParams,
) {
  const resp = await api.api_batchCreateItems(backendClient, payload, options);
  return resp;
}

export async function batchUpdateItems(
  payload: BatchUpdateItemsRequestDto,
  options?: AdditionalParams,
) {
  const resp = await api.api_batchUpdateItems(backendClient, payload, options);
  return resp;
}

export async function batchDeleteItems(
  payload: BatchDeleteItemsRequestDto,
  options?: AdditionalParams,
) {
  const resp = await api.api_batchDeleteItems(backendClient, payload, options);
  return resp;
}

export async function itemMetadataUpdate(
  itemId: string,
  payload: ItemMetadataUpdateRequestDto,
  options?: AdditionalParams,
) {
  const resp = await api.api_itemMetadataUpdate(backendClient, itemId, payload, options);
  return resp;
}

export const updateItem = (itemId: string, payload: ItemUpdateRequestDto, options?: AdditionalParams) =>
  api.api_updateItem(backendClient, itemId, payload, options);

export const updateClipList = (itemId: string, payload: ClipListUpdateDto, version: number) =>
  api.api_updateClipList(backendClient, itemId, payload, version);

export const getThumbnailUrlInTime = async (itemId: string, inPoint: number, additionalParams?: AdditionalParams): Promise<string> =>
  api.api_getThumbnailUrlInTime(backendClient, itemId, inPoint + '', additionalParams);

export const getTimeline = async (itemId: string): Promise<TimelineDto> =>
  api.api_getTimeline(backendClient, itemId);

export const updateTimeline = (itemId: string, payload: TimelineDto) =>
  api.api_updateTimeline(backendClient, itemId, payload);

/************************ operations on items ************************/

export const createVirtualClip = async (payload: VirtualClipModel | CreateVirtualClipPayload): Promise<{ id: string }> =>
  api.api_createVirtualClip(backendClient, payload);

export const saveTranscript = async (itemId: string, payload: any) =>
  api.api_saveTranscript(backendClient, itemId, payload);

export const deleteLabels = async (itemId: string, labelsToRemove: string[]) =>
  api.api_deleteLabels(backendClient, itemId, labelsToRemove);

export const startBackendJob = async (payload: StartAnalyticsJobRequestDto) =>
  api.api_startBackendJob(backendClient, payload);

export const search = async (queryParams: HttpQueryParameters) =>
  api.api_search(backendClient, { queryParams });

export const searchScroll = async (params: SearchScrollParamsType, cancelToken?: CancelToken): Promise<SearchScrollResponse> =>
  api.api_search(backendClient, { queryParams: params, cancelToken });

export const getLatestItemVersion = async (itemId: string): Promise<LatestItemVersionDto> =>
  api.api_getLatestItemVersion(backendClient, itemId);

export const createItemReplacement = async (itemId: string, payload: ItemVersion): Promise<LoadedItem> =>
  api.api_createItemReplacement(backendClient, itemId, payload);

export const updateItemMediaUsage = async (itemId: string, payload: ItemMediaUsagesRequestDto) =>
  api.api_updateItemMediaUsage(backendClient, itemId, payload);

export function getItemVersionsHistory(itemId: string): Promise<ItemVersionsHistoryDto>;
export function getItemVersionsHistory(itemId: string, fetchItemProperties: true): Promise<ItemVersionsHistoryDtoExt>;
export function getItemVersionsHistory(itemId: string, fetchItemProperties?: true): Promise<ItemVersionsHistoryDto | ItemVersionsHistoryDtoExt> {
  if (fetchItemProperties) {
    return api.api_getItemVersionsHistory(backendClient, itemId, fetchItemProperties);
  }
  return api.api_getItemVersionsHistory(backendClient, itemId);
}
export const updateItemVersion = async (itemId: string, payload: ItemVersionUpdateDto) =>
  api.api_updateItemVersion(backendClient, itemId, payload);

export const promoteOldItemVersionToLatestVersion = async (itemId: string): Promise<LoadedItem> =>
  api.api_promoteOldItemVersionToLatestVersion(backendClient, itemId);

export const getItemRelations = async (itemId: string, params?: GetItemRelationsParams): Promise<Relations> =>
  api.api_getItemRelations(backendClient, itemId, params);

export const updateItemRelations = async (itemId: string, relationType: RelationType, relatedItemIds: string[], options?: AdditionalParams): Promise<void> =>
  api.api_updateItemRelations(backendClient, itemId, relationType, relatedItemIds, options);

export const createItemRelation = async (itemId: string, relationType: RelationType, relatedItemId: string): Promise<void> =>
  api.api_createItemRelation(backendClient, itemId, relationType, relatedItemId);

export const removeItemRelation = async (itemId: string, relationType: RelationType, relatedItemId: string): Promise<void> =>
  api.api_removeItemRelation(backendClient, itemId, relationType, relatedItemId);

export const updateArchiveStatus = async (itemId: string, mustArchive: boolean, options: AdditionalParams = {}): Promise<ArchiveOperationResponseDto> => {
  const payload: ArchiveOperationRequestDto = {
    type: mustArchive ? OperationType.Archive : OperationType.UnArchive
  };
  return api.api_updateArchiveStatus(backendClient, itemId, payload, options);
};

export const getItemAnalysisStatus = async (itemId: string): Promise<AnalyticsResult[]> =>
  api.api_getItemAnalysisStatus(backendClient, itemId);

export const getItemSystemActivity = async (
  itemId: string,
  queryParams: ItemActivityQueryParams,
): Promise<ItemActivityResponseDto> =>
  api.api_getItemSystemActivity(backendClient, itemId, queryParams);

export const getTenantSystemActivity = async (queryParams: TenantSystemActivityQueryParams): Promise<SystemActivityResponseDto> =>
  api.api_getTenantSystemActivity(backendClient, queryParams);


export const getSearchItemAnalyticsResult = async (query: AnalyticsSearchQuery): Promise<AnalyticsSearchResults> =>
  api.api_getSearchItemAnalyticsResult(backendClient, query);

export const createItemUploadLock = async (itemId: string, request: CreateItemUploadLockRequestDto): Promise<CreateItemUploadLockResponseDto> =>
  api_createItemUploadLock(backendClient, itemId, request);

export const deleteItemUploadLock = async (itemId: string, options?: AdditionalParams): Promise<ApiCallResult> =>
  api_deleteItemUploadLock(backendClient, itemId, options);

export const getItemUploadLock = async (itemId: string): Promise<GetItemUploadLockResponseDto> =>
  api_getItemUploadLock(backendClient, itemId);

export const createItemUploadPartsSignedUrls = async (itemId: string, request: CreateItemUploadPartsSignedUrlsRequestDto): Promise<CreateItemUploadPartsSignedUrlsResponseDto> =>
  api_createItemUploadPartsSignedUrls(backendClient, itemId, request);

export const completeMultipartUpload = async (itemId: string, uploadLocationId: UploadLocationId, targetKey: string, uploadId: string, isCanceled: boolean, uploadParts: UploadPartDto[]): Promise<ApiCallResult> =>
  api_completeMultipartUpload(backendClient, itemId, uploadLocationId, targetKey, uploadId, isCanceled, uploadParts);

export const createAttachmentToItem = async (itemId: string, payload: CreateItemAttachmentRequestDto): Promise<string> =>
  api.api_createAttachmentToItem(backendClient, itemId, payload);

export const getAttachmentsToItem = (itemId: string, shouldExpirePosterFrameUrlEarly?: boolean): Promise<ItemAttachmentWithDownloadUrlDto[]> =>
  api.api_getAttachmentsToItem(backendClient, itemId, shouldExpirePosterFrameUrlEarly);

export const getAttachmentToItemByName = (itemId: string, attachmentFileName: string, shouldExpirePosterFrameUrlEarly?: boolean): Promise<string> =>
  api.api_getAttachmentToItemByName(backendClient, itemId, attachmentFileName, shouldExpirePosterFrameUrlEarly);

export const updateItemAttachment = async (itemId: string, payload: UpdateItemAttachmentRequestDto): Promise<ItemAttachmentDtoWithUserId> =>
  api.api_updateItemAttachment(backendClient, itemId, payload);

export const performAttachmentPrecheck = (itemId: string, payload: CreateItemAttachmentRequestDto) : Promise<AttachmentUploadPreCheckDtO> =>
  api.api_attachmentUploadPrecheck(backendClient, itemId, payload);

export const removeAttachmentFromItem = async (itemId: string, attachmentType: AttachmentType, attachmentFileName: string) =>
  api.api_removeAttachmentFromItem(backendClient, itemId, { fileName: attachmentFileName, type: attachmentType });

export const postRestoreMedia = async (payload: RestoreMediaModel) =>
  api.api_postRestoreMedia(backendClient, payload.itemId, payload);

export const getTransfers = async (supportTenantId?: string) => api.api_getTransfers(backendClient, supportTenantId);

export const generateAaf = async (itemId: string, aafRequest: AafGenerationRequest): Promise<AafDto> =>
  api.api_generateAaf(backendClient, itemId, aafRequest);

export const mergeItems = async (sourceItemId: string, targetItemId: string) =>
  api.api_mergeItems(backendClient, { sourceItemId, targetItemId });


export const scheduleHighresDeletion = async (itemId: string, payload: ScheduleHighresDeletionRequestDto) =>
  api.api_scheduleHighresDeletion(backendClient, itemId, payload);

export const unscheduleHighresDeletion = async (itemId: string) =>
  api.api_unscheduleHighresDeletion(backendClient, itemId);

export const cancelTask = async (itemId: string, taskInfo: TaskInfo) =>
  api.api_cancelTask(backendClient, itemId, taskInfo);

/************************ transfer requests ************************/

export const getTransferRequests = async (storageId: string, ignoreLock: boolean): Promise<TransferRequestDto[]> =>
  api.api_getTransferRequests(backendClient, storageId, ignoreLock, undefined);

export const postTransferRequest = async (transferRequest: TransferRequestPostRequestDto): Promise<TransferRequestDto> =>
  api.api_postTransferRequest(backendClient, transferRequest, undefined);

export const deleteTransferRequest = async (transferRequest: TransferRequestDto, isCancelled?: boolean): Promise<void> =>
  api.api_deleteTransferRequest(backendClient, transferRequest, undefined, isCancelled);

/************************ persons ************************/

export const createPerson = (name: string, description: string): Promise<any> =>
  api.api_createPerson(backendClient, name, description);

export const getPerson = (personId: string, queryParams: HttpQueryParameters = {}) =>
  api.api_getPerson(backendClient, personId, queryParams);

export const deletePerson = async (personId: string, immediate = false): Promise<any> =>
  api.api_deletePerson(backendClient, personId, immediate);

export const updatePerson = (id: string, name: string, description: string, primaryImageId?: string): Promise<any> =>
  api.api_updatePerson(backendClient, id, name, description, primaryImageId);

export const createPersonImage = (personId: string, fileName: string): Promise<PersonImageDto> =>
  api.api_createPersonImage(backendClient, personId, fileName);

export const getPersonImages = (personId: string): Promise<any> =>
  api.api_getPersonImages(backendClient, personId);

export const getPersonImage = (personId: string, personImageId: string): Promise<PersonImageDto> =>
  api.api_getPersonImage(backendClient, personId, personImageId);

export const deletePersonImage = (personId: string, personImageId: string): Promise<any> =>
  api.api_deletePersonImage(backendClient, personId, personImageId);

export const getSimilarPersons = (personId: string): Promise<SimilarPersonsDto> =>
  api.api_similarPersons(backendClient, personId);

export const mergePersons = (sourcePersonId: string, targetPersonId: string) =>
  api.api_mergePersons(backendClient, sourcePersonId, targetPersonId);

/************************ admin stuffs ************************/

export const getLatestLogId = (): Promise<string> =>
  api.api_getLatestLogId(backendClient);

export const deleteChangelog = (id: string): Promise<void> =>
  api.api_deleteChangelog(backendClient, id);

export const getSsoConfig = (): Promise<Record<string, string>> =>
  api.api_getSsoConfig(backendClient);

export const saveSsoConfig = (config: Record<string, string>): Promise<void> =>
  api.api_saveSsoConfig(backendClient, config);

export const getChangelog = (id: string): Promise<ChangelogDto> =>
  api.api_getChangelog(backendClient, id);

export const getChangelogs = (params?: { year: number, month: number }): Promise<ChangelogDto[]> =>
  api.api_getChangelogs(backendClient, params || {});

export const updateChangelog = (dto: UpdateChangelogDto): Promise<ChangelogDto> =>
  api.api_updateChangelog(backendClient, dto);

export const postChangelog = (createChangelog: CreateChangelogDto): Promise<ChangelogDto> =>
  api.api_postChangelog(backendClient, createChangelog);

export const getMeteringInfoForTenant = (tenantId: string, month: string, byService: boolean, useCache: boolean, options: AdditionalParams = {}): Promise<MeteringResultSingleTenant> => {
  const queryParameters: HttpQueryParameters = {tenantId, month, byService, isAdmin: true, useCache };
  return api.api_getMeteringInfo(backendClient, queryParameters, options);
};

export const getMeteringInfo = (queryParams: HttpQueryParameters = {}, options: AdditionalParams = {}): Promise<MeteringResponseDto> =>
  api.api_getMeteringInfo(backendClient, queryParams, options);

export const getTenants = (): Promise<TenantSlimDto[]> =>
  api.api_getTenants(backendClient);

export const getTenantIds = (): Promise<TenantIdsDto> =>
  api.api_getTenantIds(backendClient);

export const getFeatureFlags = (): Promise<FeatureFlagDto[]> =>
  api.api_getFeatureFlags(backendClient);

export const updateFeatureFlag = (id: string, dto: UpdateFeatureFlagDto): Promise<FeatureFlagDto> =>
  api.api_updateFeatureFlag(backendClient, id, dto);

export const deleteBookmark = (id: string): Promise<void> =>
  api.api_deleteBookmark(backendClient, id);

export const getBookmarks = (): Promise<BookmarkDto[]> =>
  api.api_getBookmarks(backendClient);

export const updateBookmark = (id: string, dto: UpdateBookmarkDto): Promise<BookmarkDto> =>
  api.api_updateBookmark(backendClient, id, dto);

export const createBookmark = (dto: CreateBookmarkDto): Promise<BookmarkDto> =>
  api.api_postBookmark(backendClient, dto);

export const getBookmarksV2 = (): Promise<BookmarkDto[]> =>
  api.api_getBookmarksV2(backendClient);

export const updateBookmarkV2 = (id: string, dto: UpdateBookmarkDto): Promise<BookmarkDto> =>
  api.api_updateBookmarkV2(backendClient, id, dto);

export const createBookmarkV2 = (dto: CreateBookmarkDto): Promise<BookmarkDto> =>
  api.api_postBookmarkV2(backendClient, dto);

export const readBookmarkFolders = (): Promise<BookmarkFolderWithEtag> =>
  api.api_readBookmarkFolders(backendClient);

export const writeBookmarkFolders = (dto: BookmarkWriteDto, etag: string): Promise<BookmarkFolderWithEtag> =>
  api.api_writeBookmarkFolders(backendClient, dto, etag);

export const deleteBookmarkFolders = (dto: DeleteBookmarkFolderDto, etag: string): Promise<BookmarkFolderWithEtag> =>
  api.api_deleteBookmarkFolders(backendClient, dto, etag);

export const createTenant = (payload: CreateTenantRequestDto): Promise<any> =>
  api.api_createTenant(backendClient, payload);

export const getTenant = (tenantId: string, checkAvailability: boolean = false): Promise<TenantModel | Availability> =>
  api.api_getTenant(backendClient, tenantId, checkAvailability);

export const updateTenantSettings = (tenantId: string, settings: TenantConfigDto): Promise<TenantConfigDto> =>
  api.api_updateTenantSettings(backendClient, tenantId, settings);

export const createTenantAdmin = (payload: TenantAdminPostRequestDto): Promise<UserDto> =>
  api.api_createTenantAdmin(backendClient, payload);

export const getTenantConfig = (queryParams: HttpQueryParameters = {}): Promise<TenantConfigDto> =>
  api.api_getTenantConfig(backendClient, {queryParams});

export const saveTenantConfig = (settings: UpdateTenantConfigDto): Promise<TenantConfigDto> =>
  api.api_saveTenantConfig(backendClient, settings);

export const updateTenantConfig = (settings: UpdateTenantConfigDto): Promise<TenantConfigDto> =>
  api.api_updateTenantConfig(backendClient, settings);

export const getUsers = async (): Promise<UserModelView[]> =>
  api.api_getUsers(backendClient);

export const getUserDetails = (userId: string): Promise<UserDto> =>
  api.api_getUserDetails(backendClient, userId);

export const createUser = async (payload: CreateUserDto): Promise<UserDto> =>
  api.api_createUser(backendClient, payload);

export const updateUser = async (user: UpdateUserDto): Promise<UserDto> =>
  api.api_updateUser(backendClient, user);

export const deleteUser = async (userId: string) =>
  api.api_deleteUser(backendClient, userId);

export const signOutUser = async (userId: string) =>
  api.api_signOutUser(backendClient, userId);

export const getGroups = async (): Promise<GroupModel[]> =>
  api.api_getGroups(backendClient);

export const createGroup = async (group: CreateGroupDto): Promise<GroupModel> =>
  api.api_createGroup(backendClient, group);

export const updateGroup = async (group: UpdateGroupDto): Promise<GroupModel> =>
  api.api_updateGroup(backendClient, group);

export const deleteGroup = async (groupId: string) =>
  api.api_deleteGroup(backendClient, groupId);

export const getTenantConfigDefaultExportLocation = async () =>
  api.api_getTenantConfigExportLocations(backendClient);

export const saveTenantConfigExportLocation = async (exportLocation: UpdateExternalLocationDto) =>
  api.api_saveTenantConfigExportLocation(backendClient, exportLocation);

export const deleteTenantConfigDefaultExportLocation = async () =>
  api.api_deleteTenantConfigDefaultExportLocation(backendClient);

export const getTenantConfigDefaultUploadLocation = async () =>
  api.api_getTenantConfigDefaultUploadLocation(backendClient);

export const getTenantConfigUploadLocations = async () =>
  api.api_getTenantConfigUploadLocations(backendClient);

export const getGeneratedMediaLocations = async () =>
  api.api_getGeneratedMediaLocations(backendClient);

export const saveTenantConfigDefaultUploadLocation = async (uploadLocation: UpdateDefaultUploadLocationDto) =>
  api.api_setTenantConfigDefaultUploadLocation(backendClient, uploadLocation);

export const createUploadLocation = async (uploadLocation: CreateUploadLocationDto) =>
  api.api_createUploadLocation(backendClient, uploadLocation);

export const createGeneratedMediaLocation = async (generatedMediaLocation: CreateGeneratedMediaLocationDto) =>
  api.api_createGeneratedMediaLocation(backendClient, generatedMediaLocation);

export const getGeneratedMediaLocation = async (id: string) =>
  api.api_getGeneratedMediaLocation(backendClient, id);

export const deleteGeneratedMediaLocation = async (id: string) =>
  api.api_deleteGeneratedMediaLocation(backendClient, id);

export const updateGeneratedMediaLocation = async (id: string, generatedMediaLocation: UpdateGeneratedMediaLocationDto) =>
  api.api_updateGeneratedMediaLocation(backendClient, id, generatedMediaLocation);

export const updateTenantColdStorageConfig = async (coldStorageConfig: ArchiveConfig) =>
  api.api_updateTenantColdStorageConfig(backendClient, coldStorageConfig);

export const getBucketColdStorageConfigStatus = async (configs: ColdStorageApiCommonDto[]) =>
  api.api_getBucketColdStorageConfigStatus(backendClient, configs);

export const setBucketLifecycleConfig = async (bucketName: string, configId: string, type: ColdStorageEligibleConfigTypes) => {
  const setLifecycleConfigRequest: ColdStorageApiCommonDto = {
    name: bucketName,
    configId,
    type
  };
  return api.api_setBucketLifecycleConfig(backendClient, setLifecycleConfigRequest);
};

export const getStorageManagers = (): Promise<any> =>
  api.api_getStorageManagers(backendClient);

export const getTenantStorages = (): Promise<any> =>
  api.api_getTenantStorages(backendClient);

export const createStorage = async (storage: StorageType) =>
  api.api_createStorage(backendClient, storage);

export const deleteStorage = async (storageId: string) =>
  api.api_deleteStorage(backendClient, storageId);

export const updateStorage = async (storageId: string, storage: StorageType) =>
  api.api_updateStorage(backendClient, storageId, storage);

export const getLiveSourcesV2 = (): Promise<LiveSourceDtoV2[]> =>
  api.api_getLiveSourcesV2(backendClient);

export const createLiveSourceV2 = async (source: UpdateLiveSourceDtoV2) =>
  api.api_createLiveSourceV2(backendClient, source);

export const deleteLiveSourceV2 = async (id: string) =>
  api.api_deleteLiveSourceV2(backendClient, id);

export const updateLiveSourceV2 = async (id: string, source: UpdateLiveSourceDtoV2) =>
  api.api_updateLiveSourceV2(backendClient, id, source);

export const getRouters = (): Promise<RouterDto[]> =>
  api.api_getRouters(backendClient);

export const getRouter = (routerId: string): Promise<RouterDto> =>
  api.api_getRouter(backendClient, routerId);

export const createRouter = async (router: UpdateRouterDto) =>
  api.api_createRouter(backendClient, router);

export const deleteRouter = async (id: string) =>
  api.api_deleteRouter(backendClient, id);

export const updateRouter = async (id: string, router: UpdateRouterDto) =>
  api.api_updateRouter(backendClient, id, router);

export const postRouterConnectionRequest = async (routerId: string, source: number, destination: number) =>
  api.api_postRouterConnectionRequest(backendClient, routerId, { source, destination });

export const setSelectedNdiSourceForPipeline = async (liveSourceId: string, pipelineId: string, ndiSource: NdiSourceDto) =>
  api.api_setSelectedNdiSourceForPipeline(backendClient, liveSourceId, pipelineId, { ndiSource });

export const setSelectedDynamicSourceForPipeline = async (liveSourceId: string, pipelineId: string, payload: UpdateSelectedDynamicSourceDto) =>
  api.api_setSelectedDynamicSourceForPipeline(backendClient, liveSourceId, pipelineId, payload);

export const getKeldaMediaPipelineStatus = async (liveSourceId: string, pipelineId: string): Promise<GetKeldaMediaPipelineStatusDto | null> =>
  api.api_getKeldaMediaPipelineStatus(backendClient, liveSourceId, pipelineId);

export const getExpectedLivePipelines = async () =>
  api.api_getExpectedLivePipelines(backendClient);

export const getLiveSourceStatus = async (liveSource: LiveSourceDtoV2) =>
  api.api_getLiveSourceStatus(backendClient, liveSource);

export const getSystemConfigTranscriptionLanguageSettings = async () =>
  api.api_getSystemConfigTranscriptionLanguageSettings(backendClient);

export const getAllRouterSources = async () =>
  api.api_getAllRouterSources(backendClient);

export const getNdiSourcesForTenant = async () =>
  api.api_getNdiSourcesForTenant(backendClient);

export const getDynamicSourceListForTenant = async (sourceListKey: SourceListKey) =>
  api.api_getDynamicSourceListForTenant(backendClient, sourceListKey);

export const getAllRouterControllerStatuses = async () =>
  api.api_getAllRouterControllerStatuses(backendClient);

export const getLiveLocations = async () =>
  api.api_getTenantLiveLocations(backendClient);

export const createLiveLocation = async (locationObject: CreateOrUpdateLiveLocationDto) =>
  api.api_createTenantLiveLocation(backendClient, locationObject);

export const updateLiveLocation = async (editedLocations: CreateOrUpdateLiveLocationDto, updateId: string): Promise<LiveLocationDto> =>
  api.api_updateTenantLiveLocation(backendClient, editedLocations, updateId);

export const deleteLiveLocation = async (locationIdentifier: string) =>
  api.api_deleteTenantLiveLocation(backendClient, locationIdentifier);

export const getTenantConfigTranscriptionLanguageSettings = async (queryParams: HttpQueryParameters = {}) =>
  api.api_getTenantConfigTranscriptionLanguageSettings(backendClient, { queryParams });

export const getOptionListIds = (): Promise<OptionListIdsDto> =>
  api.api_getOptionListIds(backendClient);

export const getOptionList = (id: string): Promise<OptionListDto> =>
  api.api_getOptionList(backendClient, id);

export const createOptionList = async (optionList: UpdateOptionListDto) =>
  api.api_createOptionList(backendClient, optionList);

export const deleteOptionList = async (id: string) =>
  api.api_deleteOptionList(backendClient, id);

export const updateOptionList = async (id: string, optionList: UpdateOptionListDto) =>
  api.api_updateOptionList(backendClient, id, optionList);

export const getPlayoutDevices = (): Promise<PlayoutDeviceDto[]> =>
  api.api_getPlayoutDevices(backendClient);

export const getPlayoutStatus = (): Promise<PlayoutDeviceStatusGetDto[]> =>
  api.api_getPlayoutStatus(backendClient);

export const sendPlayoutCommand = (cmd: KeldaPlayoutCommandDto | ResetPlayoutDto) =>
  api.api_sendPlayoutCommand(backendClient, cmd);

export const updateTenantConfigTranscriptionLanguageSettings = async (payload: CreateTranscriptOptionDto[]) =>
  api.api_updateTenantConfigTranscriptionLanguageSettings(backendClient, payload);

export const postDeduplicationRequest = async (payload: DeduplicationRequestDto): Promise<DeduplicationResultDto> =>
  api.api_postDeduplicationRequest(backendClient, payload);

export const adminGetMosMessages = async (
  supportTenantId: string, keldaId: string, type: MosAdminGetMessagesType, queue: MosMessageQueue, from: string,
) =>
  api.api_adminGetMosMessages(backendClient, supportTenantId, keldaId, type, queue, from);

export const postMosAdminOperation = async (supportTenantId: string, keldaId: string, payload: MosAdminOperationRequestDto) =>
  api.api_postMosQueueOperation(backendClient, supportTenantId, keldaId, payload);

export const getDisplayAspectRatios = async (): Promise<DisplayAspectRatio[]> =>
  api.api_getTenantConfigDisplayAspectRatios(backendClient);


export const updateFolderMetadata = async (folderId: string, payload: FolderMetadataUpdatePayload): Promise<FolderMetadataDto> =>
  api.api_updateFolderMetadata(backendClient, folderId, payload);

/************************ mdfs ************************/

export const getMdfs = (params: api.GetMdfsParams): Promise<any> =>
  api.api_getMdfs(backendClient, params);

export const createMdf = (request: MdfCreationRequest): Promise<MetaDataSchema> =>
  api.api_createMdf(backendClient, request)
    .then(schema => MetaDataSchema.cast(schema, true));

export const getMdf = (mdfId: string): Promise<MetaDataSchema> =>
  api.api_getMdf(backendClient, mdfId)
    .then(schema => MetaDataSchema.cast(schema, true));

export const deleteMdf = (mdfId: string) =>
  api.api_deleteMdf(backendClient, mdfId);

export const putMdf = (mdf: MetaDataSchema): Promise<MetaDataSchema> =>
  api.api_putMdf(backendClient, mdf)
    .then(schema => MetaDataSchema.cast(schema, true));

export const getTimedMetadata = async (itemId: string): Promise<TimedMetadataCollection> =>
  api.api_getTimedMetadata(backendClient, itemId);

export const updateTimedMetadata = (itemId: string, update: UpdateTimedMetadataRequestDto): Promise<void> =>
  api.api_updateTimedMetadata(backendClient, itemId, update);

/************************ user ************************/

export const getUserSettings = async (): Promise<UserSettings | null> =>
  api.api_getUserSettings(backendClient);

export const saveUserSettings = async (settings: UserSettings): Promise<UserSettings> =>
  api.api_saveUserSettings(backendClient, settings);

export const createAvatarUploadURL = async (dto: UserAvatarUploadUrlRequest): Promise<UserAvatarUploadDto> =>
  api.api_createAvatarUploadURL(backendClient, dto);

export const deleteUserAvatar = async (): Promise<void> =>
  api.api_deleteUserAvatar(backendClient);

export const getAllUsers = async (): Promise<UserInfoDto[]> =>
  api.api_getAllUsers(backendClient);

export const getUserStatus = async (): Promise<UserStatusDto>=>
  api.api_getUserStatus(backendClient);

/************************ stats ************************/

export const getStatistics = (queryParams: GetCountStatisticsQueryParams = {}): Promise<CountStatisticsResponseDto> =>
  api.api_getStatistics(backendClient, queryParams);

export const logStatistic = (body: LogStaticRequestDto): Promise<void> =>
  api.api_logStatistic(backendClient, body);

/************************
 * IngestStorageConfig
 */
export const getIngestStorageConfigs = async (): Promise<IngestStorageConfigClientDto[]> =>
  api.api_getIngestStorageConfigs(backendClient);

export const getIngestStorageConfig = async (itemId: string): Promise<IngestStorageConfigClientDto> =>
  api.api_getIngestStorageConfig(backendClient, itemId);

export const createIngestStorageConfig = async (body: IngestStorageConfigClientDto): Promise<IngestStorageConfigClientDto> =>
  api.api_createIngestStorageConfig(backendClient, body);

export const updateIngestStorageConfig = async (itemId: string, body: IngestStorageConfigClientDto): Promise<IngestStorageConfigClientDto> =>
  api.api_updateIngestStorageConfig(backendClient, itemId, body);

export const updateUploadLocation = async (itemId: string, body: UpdateUploadLocationDto): Promise<IngestStorageConfigClientDto> =>
  api.api_updateIngestStorageConfig(backendClient, itemId, body);

export const deleteIngestStorageConfig = async (itemId: string): Promise<string> =>
  api.api_deleteIngestStorageConfig(backendClient, itemId);

/************************ New location entities ************************/
export const createLocationConfig = async (body: CreateMinioConfigDto, keldaConfigId?: string): Promise<MinioConfigDto> =>
  api.api_createLocationConfig(backendClient, body, keldaConfigId);

export const updateLocationConfig = async (body: UpdateMinioConfigDto): Promise<MinioConfigDto> =>
  api.api_updateLocationConfig(backendClient, body);

export const deleteLocationConfig = async (locationConfigId: string): Promise<void> =>
  api.api_deleteLocationConfig(backendClient, locationConfigId);

/************************ Storage location verification ************************/
export const verifyExternalStorage = async (verificationRequest: ExternalStorageVerificationRequest): Promise<ExternalStorageVerificationResponse> =>
  api.api_verifyExternalStorage(backendClient, verificationRequest);

/************************ Catch up ingest of storage ************************/
export const catchUpStorageIngest = async (catchUpRequest: CatchupIngestStorageRequest): Promise<void> =>
  api.api_catchUpStorageIngest(backendClient, catchUpRequest);

/************************ ingest file from external bucket after upload completes from the client  ************************/
export const ingestFile = async (dto: IngestFileDto): Promise<void> =>
  api.api_ingestFile(backendClient, dto);

// Reprocessing item
export const reprocessItem = (itemId: string, reprocessBy: string, reprocessActionType: ReprocessActionType, supportTenantId?: string): Promise<Partial<LoadedItem>> =>
  api.api_reprocessItem(backendClient, itemId, reprocessBy, reprocessActionType, supportTenantId);

/************************ Live recording ************************/
export const createNonScheduledLiveRecordingItem = (dto: NonScheduledLiveItemCreationRequestDto): Promise<NonScheduledLiveItemCreationResponseDto> =>
  api.api_createLiveRecordingItem(backendClient, dto);

export const getLiveItemRecordingState = (itemId: string, supportTenantId?: string): Promise<LiveRecordingItemStateDto> =>
  api.api_getLiveItemRecordingState(backendClient, itemId, supportTenantId);

export const performLiveItemAction = (itemId: string, dto: LiveItemStateRequestDto, supportTenantId?: string): Promise<LiveRecordingItemStateDto> =>
  api.api_performLiveItemAction(backendClient, itemId, dto, supportTenantId);

export const createScheduledRecording = (dto: ScheduledRecordingCreationRequestDto): Promise<ScheduledRecordingDto> =>
  api.api_createScheduledRecording(backendClient, dto);

export const getScheduledRecording = (itemId: string): Promise<ScheduledRecordingDto> =>
  api.api_getScheduledRecording(backendClient, itemId);

export const putScheduledRecording = (itemId: string, dto: ScheduledRecordingPutRequestDto): Promise<ScheduledRecordingDto> =>
  api.api_putScheduledRecording(backendClient, itemId, dto);

export const deleteScheduledRecording = (itemId: string): Promise<void> =>
  api.api_deleteScheduledRecording(backendClient, itemId);

export const getScheduledRecordings = (from: number, to: number): Promise<GetScheduledRecordingsDto> =>
  api.api_getScheduledRecordings(backendClient, from, to);

export const createRecordingSeries = (dto: RecordingSeriesCreationDto): Promise<RecordingSeriesFullDto> =>
  api.api_createRecordingSeries(backendClient, dto);

export const getRecordingSeries = (seriesId: string): Promise<RecordingSeriesFullDto> =>
  api.api_getRecordingSeries(backendClient, seriesId);

export const updateRecordingSeries = (seriesId: string, dto: RecordingSeriesUpdateDto): Promise<RecordingSeriesFullDto> =>
  api.api_updateRecordingSeries(backendClient, seriesId, dto);

export const createRecordingSeriesItem = (seriesId: string, dto: RecordingSeriesItemCreationDto): Promise<ScheduledRecordingDto> =>
  api.api_createRecordingSeriesItem(backendClient, seriesId, dto);

export const deletePlannedSeriesItem = (seriesId: string, scheduledStartTimeMs: number): Promise<void> =>
  api.api_deletePlannedSeriesItem(backendClient, seriesId, scheduledStartTimeMs);


export const getItemEditStatuses = (itemId: string) => {
  return api.api_getItemEditStatuses(backendClient, itemId);
};

export const postItemEditStatus = (itemId: string, payload: ItemEditStatusCreateDto): Promise<string> => {
  return api.api_postItemEditStatus(backendClient, itemId, payload);
};

export const deleteItemEditStatus = (itemId: string, payload: ItemEditStatusDeleteDto): Promise<string> => {
  return api.api_deleteItemEditStatus(backendClient, itemId, payload);
};

export const getIPAddress = (): Promise<IPAddressDto> => {
  return api.api_getIPAddress(backendClient);
};

export const getShares = (
  allForTenant: boolean,
  token?: string,
  startDate?: string,
  endDate?: string
): Promise<{ shares: ExternalShareGetDto[], nextToken: string | null }> =>
  api.api_getShares(backendClient, allForTenant, undefined, token, startDate, endDate);

export const getSharesForContent = (contentId: string, allForTenant: boolean): Promise<{ shares: ExternalShareGetDto[], nextToken: string | null }> =>
  api.api_getShares(backendClient, allForTenant, contentId);

export const createShare = (dto: ShareCreationRequestDto): Promise<ExternalShareGetDto> =>
  api.api_createShare(backendClient, dto);

export const getShare = (shareSecret: string): Promise<ExternalShareGetDto> =>
  api.api_getShare(backendClient, shareSecret);

export const getCustomActions = (): Promise<CustomActionDto[]> =>
  api.api_getCustomActions(backendClient);

export const createCustomAction = (dto: CreateCustomActionDto): Promise<CustomActionDto> =>
  api.api_createCustomAction(backendClient, dto);

export const updateCustomAction = (id: string, dto: UpdateCustomActionDto): Promise<void> =>
  api.api_updateCustomAction(backendClient, id, dto);

export const deleteCustomAction = (id: string): Promise<void> =>
  api.api_deleteCustomAction(backendClient, id);

export const getWebhooks = (): Promise<WebhookDto[]> =>
  api.api_getWebhooks(backendClient);

export const createWebhook = (dto: CreateWebhookDto): Promise<WebhookDto> =>
  api.api_createWebhook(backendClient, dto);

export const updateWebhook = (id: string, dto: UpdateWebhookDto): Promise<void> =>
  api.api_updateWebhook(backendClient, id, dto);

export const deleteWebhook = (id: string): Promise<void> =>
  api.api_deleteWebhook(backendClient, id);


export const getCustomIcons = (): Promise<CustomIconDto[]> =>
  api.api_getCustomIcons(backendClient);

export const createCustomIcon = (customIconDto: CreateCustomIconDto): Promise<CustomIconUploadDto> =>
  api.api_createCustomIcon(backendClient, customIconDto);

export const deleteCustomIcon = (id: string): Promise<void> =>
  api.api_deleteCustomIcon(backendClient, id);

export const updateShare = (shareSecret: string, dto: ShareUpdateRequestDto): Promise<ExternalShareGetDto> =>
  api.api_updateShare(backendClient, shareSecret, dto);

export const deleteShare = (shareSecret: string): Promise<void> =>
  api.api_deleteShare(backendClient, shareSecret);

export const getUserTask = (assignee: string, taskId: string): Promise<GetUserTaskResponseDto> =>
  api.api_getUserTask(backendClient, assignee, taskId);

export const getUserTasks = (assignee: string, params: GetUserTasksQueryParams): Promise<GetUserTasksResponseDto> =>
  api.api_getUserTasks(backendClient, assignee, params);

export const getMediaSegmentUrls = (itemId: string, streamIds: StreamRoleId[],
                                 signedForMethod: 'get' | 'put',
                                 firstSegmentIndex: number): Promise<MediaSegmentsResponse> =>
  api.api_getMediaSegmentUrls(backendClient, itemId, streamIds, signedForMethod, firstSegmentIndex);

export const getPendingOperationsSummary = (ignoreEmptyRows: boolean): Promise<TenantOpsSummaryReport[]> =>
  api.api_getPendingOperationsSummary(backendClient, ignoreEmptyRows);

export const createRenderSequenceRequest = async (payload: RenderSequenceDto): Promise<RenderSequencePostResponseDto> =>
  api.api_createRenderSequenceRequest(backendClient, payload);

export function getSecureApiKeys(): Promise<SecureApiKeyDto[]> {
  return api.api_getSecureApiKeys(backendClient);
}

export function postSecureApiKeys(req: SecureApiKeyModifyRequestDto): Promise<CreateSecureApiKeyResponseDto> {
  return api.api_postSecureApiKeys(backendClient, req);
}

export function putSecureApiKey(keyId: string, req: SecureApiKeyModifyRequestDto): Promise<SecureApiKeyDto> {
  return api.api_putSecureApiKey(backendClient, keyId, req);
}

export function deleteSecureApiKey(keyId: string): Promise<void> {
  return api.api_deleteSecureApiKey(backendClient, keyId);
}

export function postCropImage(payload: CropImageRequest) {
  return api_postCropImage(backendClient, payload);
}

export function getPlugins(): Promise<PluginDto[]> {
  return api.api_getPlugins(backendClient);
}

export function getPlugin(pluginId: string): Promise<PluginDto> {
  return api.api_getPlugin(backendClient, pluginId);
}

export function createPlugin(payload: CreatePluginDto): Promise<PluginDto> {
  return api.api_createPlugin(backendClient, payload);
}

export function updatePlugin(pluginId: string, payload: UpdatePluginDto): Promise<void> {
  return api.api_updatePlugin(backendClient, pluginId, payload);
}
export function deletePlugin(pluginId: string): Promise<void> {
  return api.api_deletePlugin(backendClient, pluginId);
}
